import React, { useRef, forwardRef, useImperativeHandle } from 'react';
import List, { ListItemBasic, ListItemText } from 'components/List';
import Menu, { MenuProps, MenuRef } from 'components/Menu';

const MENU_CONFIG = {
  usePortal: true,
  positioningOptions: {
    placement: 'bottom-start',
    modifiers: [
      {
        name: 'preventOverflow',
        options: {
          altAxis: true,
        },
      },
      {
        name: 'offset',
        options: {
          offset: [-8, 4]
        }
      },
      {
        name: 'flip',
        enabled: true,
      }
    ]
  }
};

export type TextwChipsListHandle = {
  positionMenu: () => void
};
export interface TextwChipsListProps extends Omit<React.ComponentPropsWithoutRef<'div'>, 'contextmenu'> {
  /** Object of list items and the event triggering the List. */
  list?: {
    event?: {},
    items: {
      label?: string,
      itemNode?: React.ReactNode,
      key?: string,
      content?: React.ReactNode,
      searchPattern?: string,
      onClick?: React.MouseEventHandler,
      submitChip?: boolean
    }[]
  },
  /** The element reference that triggered the menu. */
  triggerEl?: React.RefObject<HTMLElement>;
  /** Reference to pass to the List. */
  listRef?: React.RefObject<HTMLElement>;
  /** The height of the triggering component. Used to calculate list max height.  */
  contentHeight?: string | number;
  /** Override triggerList `Menu` positioning options based on popper (https://popper.js.org/docs/v2/). */
  menuProps?: Partial<MenuProps>;
  /**
   * Callback fired when the component requests to be closed.
   *
   * @param {object} event The event source of the callback.
   */
  onClose?: (args: React.MouseEvent | React.KeyboardEvent) => void;
}

const TextwChipsList = forwardRef<TextwChipsListHandle, TextwChipsListProps>((props: TextwChipsListProps, ref) => {
  const {
    list,
    triggerEl,
    contentHeight,
    listRef,
    menuProps,
    onClose,
    ...otherProps
  } = props;
  const { style: listStyle, ...restOfOtherProps } = otherProps;
  if (!list) {
    return null;
  }
  const {
    event,
    items
  } = list;
  let virtualEl;
  const menuRef = useRef<MenuRef>(null);
  useImperativeHandle(ref, () => ({
    positionMenu: () => {
      menuRef.current && menuRef.current.positionMenu();
    }
  }));
  // ContextualMenu should be rendered next to the pointer so a virtual target is used
  if (event && (event as MouseEvent).type === 'contextmenu') {
    const {
      pageX,
      pageY
    } = event as MouseEvent;
    virtualEl = {
      current: {
        contains: () => false,
        getBoundingClientRect: () => ({
          width: 0,
          height: 0,
          top: pageY,
          right: pageX,
          bottom: pageY,
          left: pageX,
        }),
      }
    };
  }

  return (
    <Menu
      ref={menuRef}
      onClose={onClose}
      {...{
        isOpen: true,
        triggerEl: virtualEl || triggerEl,
        ...MENU_CONFIG
      }}
      {...menuProps}
    >
      <List
        ref={listRef}
        style={{ maxHeight: contentHeight ? `calc(50vh - ${contentHeight})` : '50vh', ...listStyle }}
        {...restOfOtherProps}
      >
        {items.map(menuItem => (
          <ListItemBasic
            key={menuItem.label}
            {...{ ...menuItem, label: undefined }}
          >
            <ListItemText>
              {menuItem.label}
            </ListItemText>
          </ListItemBasic>
        ))}
      </List>
    </Menu>
  );
});

TextwChipsList.defaultProps = {
  list: undefined,
  triggerEl: {
    current: null
  },
  listRef: {
    current: null
  },
  contentHeight: undefined,
  menuProps: {},
  onClose: undefined
};

TextwChipsList.displayName = 'TextwChipsList';
export default TextwChipsList;
